import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiMinus, FiPlus, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import { formatCurrency, getShippingCost } from "../config/siteConfig";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [failedImages, setFailedImages] = useState([]);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const shipping = getShippingCost(subtotal);

  const changeQuantity = (item, nextQuantity) => {
    if (nextQuantity <= 0) {
      removeFromCart(item.id, item.size, item.color);
      return;
    }
    updateQuantity(item.id, item.size, item.color, nextQuantity);
  };

  if (lines.length === 0) {
    return (
      <main id="main-content" className="cart-empty">
        <p className="eyebrow">Your basket / 00</p>
        <h1>Your basket is empty.</h1>
        <p>Browse useful objects selected for thoughtful everyday living.</p>
        <Link className="button-outline" to="/shop">
          Explore the collection <FiArrowRight aria-hidden="true" />
        </Link>
      </main>
    );
  }

  return (
    <main id="main-content" className="cart-page">
      <div className="cart-page__layout">
        <section className="cart-page__items" aria-labelledby="basket-heading">
          <header className="cart-page__heading">
            <h1 id="basket-heading">Your basket</h1>
            <span>{String(lines.length).padStart(2, "0")}</span>
          </header>

          <div className="cart-lines">
            {lines.map((item) => {
              const key = `${item.id}-${item.size}-${item.color}`;
              const lineTotal = item.product.price * item.quantity;
              const failed = failedImages.includes(key);

              return (
                <article className="cart-line" key={key}>
                  <Link
                    className={`cart-line__image${failed ? " image-fallback" : ""}`}
                    to={`/shopDetails/${item.id}`}
                    aria-label={`View ${item.product.name}`}
                  >
                    {!failed && (
                      <img
                        src={item.product.image}
                        alt={item.product.name}
                        width="1120"
                        height="1400"
                        loading="lazy"
                        decoding="async"
                        onError={() => setFailedImages((images) => [...images, key])}
                      />
                    )}
                  </Link>

                  <div className="cart-line__details">
                    <div>
                      <Link to={`/shopDetails/${item.id}`}>{item.product.name}</Link>
                      <em>{item.product.maker}</em>
                      {(item.color || item.size) && (
                        <p>
                          {[item.color, item.size].filter(Boolean).join(" / ")}
                        </p>
                      )}
                    </div>
                    <button
                      className="cart-line__remove"
                      type="button"
                      onClick={() => removeFromCart(item.id, item.size, item.color)}
                    >
                      <FiX aria-hidden="true" /> Remove
                    </button>
                  </div>

                  <div className="cart-line__purchase">
                    <div className="quantity-control" aria-label={`Quantity for ${item.product.name}`}>
                      <button
                        type="button"
                        onClick={() => changeQuantity(item, item.quantity - 1)}
                        aria-label={`Decrease ${item.product.name} quantity`}
                      >
                        <FiMinus aria-hidden="true" />
                      </button>
                      <span aria-live="polite">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => changeQuantity(item, item.quantity + 1)}
                        disabled={item.quantity >= item.product.stock}
                        aria-label={`Increase ${item.product.name} quantity`}
                      >
                        <FiPlus aria-hidden="true" />
                      </button>
                    </div>
                    <strong>{formatCurrency(lineTotal)}</strong>
                  </div>
                </article>
              );
            })}
          </div>

          <Link className="text-link cart-page__continue" to="/shop">
            ← Continue shopping
          </Link>
        </section>

        <aside className="cart-summary" aria-label="Basket summary">
          <h2>Cart subtotal</h2>
          <dl>
            <div>
              <dt>Subtotal</dt>
              <dd>{formatCurrency(subtotal)}</dd>
            </div>
            <div>
              <dt>Tracked delivery</dt>
              <dd>{shipping === 0 ? "Complimentary" : formatCurrency(shipping)}</dd>
            </div>
            <div className="cart-summary__total">
              <dt>Estimated total</dt>
              <dd>{formatCurrency(subtotal + shipping)}</dd>
            </div>
          </dl>
          <p>Shipping and taxes are confirmed during checkout.</p>
          <p>
            Read our <Link className="text-link" to="/shipping">shipping</Link> and{" "}
            <Link className="text-link" to="/terms">terms</Link> information.
          </p>
          <button
            className="cart-summary__checkout"
            type="button"
            onClick={() => {
              navigate("/checkout");
              window.scrollTo({ top: 0 });
            }}
          >
            Proceed to checkout <FiArrowRight aria-hidden="true" />
          </button>
          <Link className="cart-summary__shop text-link" to="/shop">
            or Continue shopping
          </Link>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
